import type { TemplateTokenContext } from "@unbox/api";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { useStoreComposerImage } from "@/api/message-images";
import { useMessageTemplates } from "@/api/message-templates";
import { Toggle } from "@/components/base/toggle/toggle";
import { type ComposerImage, composerImageFromStored } from "@/components/message-composer/composer-image";
import { type ComposerPrimaryAction, MessageComposer } from "@/components/message-composer/message-composer";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const HOUR_OPTIONS = Array.from({ length: 12 }, (_, i) => i + 1);
const TIMEZONE_OPTIONS = Intl.supportedValuesOf("timeZone");
const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

function to12Hour(hour24: number): { hour: number; period: "AM" | "PM" } {
  const period = hour24 >= 12 ? "PM" : "AM";
  const hour = hour24 % 12 || 12;
  return { hour, period };
}

function to24Hour(hour12: number, period: "AM" | "PM"): number {
  if (period === "AM") return hour12 === 12 ? 0 : hour12;
  return hour12 === 12 ? 12 : hour12 + 12;
}

export type AutomatedMessageValue = {
  messageTemplateId: string | null;
  customMessage: string | null;
  imageId: string | null;
  imageUrl: string | null;
  sendAtHour: number | null;
  sendAtTimezone: string | null;
};

type MessagePart = Pick<AutomatedMessageValue, "messageTemplateId" | "customMessage" | "imageId" | "imageUrl">;
export type SendAtPart = Pick<AutomatedMessageValue, "sendAtHour" | "sendAtTimezone">;
export type MessageDraft = Pick<AutomatedMessageValue, "messageTemplateId" | "customMessage"> & {
  image: ComposerImage | null;
};

export const hasMessageContent = (value: MessageDraft) => !!value.messageTemplateId || !!value.customMessage?.trim();

export function useCommitMessageDraft() {
  const { storeWithUrl, isPending } = useStoreComposerImage();
  const commit = async (draft: MessageDraft): Promise<MessagePart> => {
    const stored = draft.messageTemplateId ? null : await storeWithUrl(draft.image);
    return {
      messageTemplateId: draft.messageTemplateId,
      customMessage: draft.customMessage,
      imageId: stored?.imageId ?? null,
      imageUrl: stored ? (stored.imageUrl ?? draft.image?.previewUrl ?? null) : null,
    };
  };
  return { commit, isPending };
}

export function AutomatedMessageFields({
  initialValue,
  resetKey,
  variableContext,
  onChange,
  primaryAction,
  autoFocus,
  merchantResources = true,
  children,
}: {
  initialValue?: MessagePart;
  resetKey: string | number;
  variableContext: TemplateTokenContext;
  onChange: (value: MessageDraft) => void;
  primaryAction?: ComposerPrimaryAction;
  autoFocus?: boolean;
  merchantResources?: boolean;
  children?: ReactNode;
}) {
  const { data: templates = [], isLoading: isLoadingTemplates } = useMessageTemplates();
  const [templateId, setTemplateId] = useState<string | null>(initialValue?.messageTemplateId ?? null);
  const [message, setMessage] = useState("");
  const [image, setImage] = useState<ComposerImage | null>(() =>
    composerImageFromStored(initialValue?.imageId, initialValue?.imageUrl),
  );

  const isUnchangedTemplate = (id: string | null) => {
    const template = templates.find((t) => t.id === id);
    if (!template || template.content.trim() !== message.trim()) return false;
    return (template.imageId ?? null) === ((merchantResources ? image?.imageId : null) ?? null);
  };

  const seededFor = useRef<string | number | null>(null);

  useEffect(() => {
    if (seededFor.current === resetKey) return;
    if (initialValue?.messageTemplateId && isLoadingTemplates) return;
    seededFor.current = resetKey;
    const template = templates.find((t) => t.id === initialValue?.messageTemplateId);
    setTemplateId(initialValue?.messageTemplateId ?? null);
    setMessage(template ? template.content : (initialValue?.customMessage ?? ""));
    setImage(
      composerImageFromStored(initialValue?.imageId, initialValue?.imageUrl) ??
        composerImageFromStored(template?.imageId, template?.imageUrl),
    );
  }, [resetKey, templates, isLoadingTemplates]);

  const report = useRef(onChange);
  report.current = onChange;

  useEffect(() => {
    const linked = isUnchangedTemplate(templateId);
    report.current({
      messageTemplateId: linked ? templateId : null,
      customMessage: linked ? null : message || null,
      image: merchantResources ? image : null,
    });
  }, [message, templateId, image, templates]);

  return (
    <MessageComposer
      key={resetKey}
      value={message}
      onChange={setMessage}
      image={merchantResources ? image : null}
      onImageChange={merchantResources ? setImage : undefined}
      templates={merchantResources}
      variableContext={variableContext}
      initialTemplateId={initialValue?.messageTemplateId ?? null}
      onTemplateChange={setTemplateId}
      primaryAction={primaryAction}
      autoFocus={autoFocus}
    >
      {children}
    </MessageComposer>
  );
}

/**
 * The send-at half. Returns its toggle and its selects separately, because the dialog puts the
 * toggle in its footer with the selects beneath while an inline host stacks them together.
 */
export function useSendAtControls({
  initialValue,
  resetKey,
  onChange,
}: {
  initialValue?: SendAtPart;
  resetKey: string | number;
  onChange: (value: SendAtPart) => void;
}): { toggle: ReactNode; selects: ReactNode; value: SendAtPart } {
  const [enabled, setEnabled] = useState((initialValue?.sendAtHour ?? null) !== null);
  const [hour12, setHour12] = useState(() =>
    initialValue?.sendAtHour != null ? to12Hour(initialValue.sendAtHour).hour : 9,
  );
  const [period, setPeriod] = useState<"AM" | "PM">(() =>
    initialValue?.sendAtHour != null ? to12Hour(initialValue.sendAtHour).period : "AM",
  );
  const [timezone, setTimezone] = useState(initialValue?.sendAtTimezone ?? userTimezone);

  useEffect(() => {
    setEnabled((initialValue?.sendAtHour ?? null) !== null);
    setHour12(initialValue?.sendAtHour != null ? to12Hour(initialValue.sendAtHour).hour : 9);
    setPeriod(initialValue?.sendAtHour != null ? to12Hour(initialValue.sendAtHour).period : "AM");
    setTimezone(initialValue?.sendAtTimezone ?? userTimezone);
  }, [resetKey]);

  const sendAtValue = (next: {
    enabled: boolean;
    hour12: number;
    period: "AM" | "PM";
    timezone: string;
  }): SendAtPart =>
    next.enabled
      ? { sendAtHour: to24Hour(next.hour12, next.period), sendAtTimezone: next.timezone }
      : { sendAtHour: null, sendAtTimezone: null };

  const current = { enabled, hour12, period, timezone };

  return {
    value: sendAtValue(current),
    toggle: (
      <Toggle
        label="Send at set time"
        isSelected={enabled}
        onChange={(next) => {
          setEnabled(next);
          onChange(sendAtValue({ ...current, enabled: next }));
        }}
      />
    ),
    selects: enabled ? (
      <div className="mt-2 flex items-center gap-2 px-1">
        <Select
          value={String(hour12)}
          onValueChange={(v) => {
            setHour12(Number(v));
            onChange(sendAtValue({ ...current, hour12: Number(v) }));
          }}
        >
          <SelectTrigger className="w-20">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {HOUR_OPTIONS.map((h) => (
              <SelectItem key={h} value={String(h)}>
                {h}:00
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select
          value={period}
          onValueChange={(v) => {
            setPeriod(v as "AM" | "PM");
            onChange(sendAtValue({ ...current, period: v as "AM" | "PM" }));
          }}
        >
          <SelectTrigger className="w-20">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="AM">AM</SelectItem>
            <SelectItem value="PM">PM</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={timezone}
          onValueChange={(v) => {
            setTimezone(v);
            onChange(sendAtValue({ ...current, timezone: v }));
          }}
        >
          <SelectTrigger className="min-w-0 flex-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TIMEZONE_OPTIONS.map((tz) => (
              <SelectItem key={tz} value={tz}>
                {tz.replace(/_/g, " ")}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    ) : null,
  };
}
